// Copyright (c) 2026, Orgain and contributors
// For license information, please see license.txt

/**
 * EmailAccountForm - the full native Email Account editor, mirroring the
 * Frappe desk form so admins never open /app. Sections: Account ·
 * Authentication (Basic / OAuth) · Incoming (POP/IMAP) · Outgoing (SMTP) ·
 * Signature / Auto Reply / Footer. For OAuth accounts, the Authorize button
 * drives the provider consent popup (no desk). The Connected App itself is
 * provisioned in the Frappe backend - this form only binds to it.
 */

import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Loader2,
  Save,
  Eye,
  EyeOff,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  Beaker,
  AlertTriangle,
} from 'lucide-react';

import {
  brainConfigService,
  runEmailOAuthPopup,
  type EmailAccount,
  type ConnectedAppSummary,
} from '@/services/kbService';
import { logger } from '@/utils/logger';
import { getFriendlyErrorMessage } from '@/utils/errorMessage';
import { notifySetupStatusChanged } from '@/utils/setupStatus';

type Form = Partial<EmailAccount> & { email_id: string; password: string };

// The "Service" picker is really a connection-mode picker - it bundles the
// Frappe `service` + `auth_method` choice into one control so the operator
// doesn't have to reason about auth methods separately:
//   custom    → blank service, Basic auth, manual SMTP
//   gmail_app → GMail service, Basic auth, Google App Password
//   oauth     → OAuth auth for Outlook (Gmail uses the app-password mode)
type ConnMode = 'custom' | 'gmail_app' | 'oauth';
const MODES: Array<{ value: ConnMode; label: string }> = [
  { value: 'gmail_app', label: 'Gmail (Password Authentication)' },
  { value: 'oauth', label: 'OAuth (Outlook)' },
  { value: 'custom', label: 'Custom server' },
];

// service -> SMTP host (used to prefill so the operator needn't know it).
const SERVICE_PRESETS: Record<string, { smtp: string }> = {
  GMail: { smtp: 'smtp.gmail.com' },
  'Outlook.com': { smtp: 'smtp.office365.com' },
};

const OAUTH_SMTP = {
  smtp_server: SERVICE_PRESETS['Outlook.com'].smtp,
  smtp_port: '587',
  use_tls: true,
  use_ssl_for_outgoing: false,
} as const;

// Gmail's SMTP endpoint is fixed (smtp.gmail.com:587 + TLS), so - exactly like
// OAuth/Outlook - the host and port are not shown. They stay in form state and
// are still sent on save; the operator only supplies the app password.
const GMAIL_SMTP = {
  smtp_server: SERVICE_PRESETS.GMail.smtp,
  smtp_port: '587',
  use_tls: true,
  use_ssl_for_outgoing: false,
} as const;

const newForm = (): Form => ({
  email_id: '',
  email_account_name: '',
  // Default a new account to "Gmail (Password Authentication)" - service +
  // Basic auth + preset Gmail SMTP host, so the operator only adds an app
  // password. (onModeChange resets these if they switch modes.)
  service: 'GMail',
  auth_method: 'Basic',
  password: '',
  awaiting_password: false,
  connected_app: '',
  enable_outgoing: true,
  default_outgoing: false,
  use_tls: true,
  use_ssl_for_outgoing: false,
  smtp_server: 'smtp.gmail.com',
  smtp_port: '587',
  // Default off for new accounts - Orgain notification senders don't want the
  // List-Unsubscribe header or the tracking pixel. Backend mirrors this in
  // _apply_new_account_defaults; the operator can still enable track status
  // from the toggle, which the backend respects.
  send_unsubscribe_message: false,
  track_email_status: false,
  no_smtp_authentication: false,
  always_bcc: '',
  enable_incoming: false,
  use_ssl: false,
  use_starttls: false,
  email_server: '',
  incoming_port: '',
  email_sync_option: 'UNSEEN',
  initial_sync_count: '250',
  attachment_limit: 25,
  add_signature: false,
  signature: '',
  enable_auto_reply: false,
  auto_reply_message: '',
  footer: '',
});

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Existing account name to edit, or null to create. */
  accountName?: string | null;
  onSaved: (name: string) => void;
}

export default function EmailAccountForm({ open, onOpenChange, accountName, onSaved }: Props) {
  const [f, setF] = useState<Form>(newForm());
  const [savedName, setSavedName] = useState<string | null>(accountName || null);
  const [original, setOriginal] = useState<Form | null>(null);
  const [apps, setApps] = useState<ConnectedAppSummary[]>([]);
  const [appsError, setAppsError] = useState(false);
  const [appsLoading, setAppsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [authorizing, setAuthorizing] = useState(false);
  // Authorize writes the account before opening the consent popup, so the
  // button reports the two steps separately rather than a vague "Authorizing…".
  const [authPhase, setAuthPhase] = useState<'saving' | 'consent'>('saving');
  // Reactive admin-consent (Option B): tenant admin must approve before this
  // mailbox can authorize. Shown as a non-blocking notice with a retry.
  const [authNotice, setAuthNotice] = useState<string | null>(null);
  // Wrong-account sign-in: nothing was applied server-side, the token is parked.
  // We ask before adopting, because this rewrites the address the mailbox sends
  // from - never a silent change.
  const [mismatch, setMismatch] = useState<
    { identity: string; expected: string; confirmToken: string } | null
  >(null);
  const [adopting, setAdopting] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const set = (p: Partial<Form>) => setF((prev) => ({ ...prev, ...p }));

  const loadApps = useCallback(async () => {
    setAppsLoading(true);
    try {
      setApps(await brainConfigService.listConnectedApps());
      setAppsError(false);
    } catch (err) {
      logger.error('Failed to list connected apps', err);
      setApps([]);
      setAppsError(true);
    } finally {
      setAppsLoading(false);
    }
  }, []);

  /** Re-read one account into the form and make it the new clean baseline. */
  const reload = useCallback(async (name: string) => {
    const acc = await brainConfigService.getEmailAccount(name);
    const fresh = { ...newForm(), ...acc, password: '' };
    setF(fresh);
    setOriginal(fresh);
    setSavedName(name);
  }, []);

  // Open-time load ONLY. Never call this to refresh after the dialog created an
  // account: it keys off the `accountName` PROP, which is still null for an
  // "Add" - so it would wipe the just-created account back to a blank new form.
  // Use reload(savedName) for that.
  const init = useCallback(async () => {
    setLoading(true);
    try {
      await loadApps();
      if (accountName) {
        await reload(accountName);
      } else {
        setF(newForm());
        setOriginal(null);
        setSavedName(null);
      }
    } catch (err) {
      logger.error('Failed to load email account', err);
      toast.error('Could not load the account');
    } finally {
      setLoading(false);
    }
  }, [accountName, loadApps, reload]);

  useEffect(() => {
    if (open) void init();
  }, [open, init]);

  const isGmail = (f.service || '').toLowerCase().includes('gmail');
  const isOAuth = f.auth_method === 'OAuth';

  // Collapse (service, auth_method) into the single connection mode the
  // picker exposes.
  const modeOf = (form: Partial<Form>): ConnMode =>
    form.auth_method === 'OAuth'
      ? 'oauth'
      : (form.service || '').toLowerCase().includes('gmail')
        ? 'gmail_app'
        : 'custom';
  const mode: ConnMode = isOAuth ? 'oauth' : isGmail ? 'gmail_app' : 'custom';
  // The service the account was actually saved with (null for a new account).
  const savedMode: ConnMode | null = original ? modeOf(original) : null;

  const onModeChange = (m: ConnMode) => {
  // Returning to the account's ORIGINAL service restores its saved values
  // (email, name, server, connected app) rather than leaving them blank.
  if (original && m === savedMode) {
    setF({ ...original, password: '' });
    return;
  }
  const identityReset = savedName ? { email_id: '' } : { email_id: '', email_account_name: '' };
  if (m === 'custom') {
    // Manual SMTP - clear any preset host/port/password and the OAuth app so
    // the operator starts from a clean slate and enters their own server.
    set({
      ...identityReset,
      service: '',
      auth_method: 'Basic',
      connected_app: '',
      smtp_server: '',
      smtp_port: '587',
      password: '',
    });
  } else if (m === 'gmail_app') {
    // Gmail SMTP is fully preset (smtp.gmail.com:587 + TLS) - the operator
    // only supplies the app password.
    set({
      ...identityReset,
      service: 'GMail',
      auth_method: 'Basic',
      ...GMAIL_SMTP,
      connected_app: '',
      // A custom-server password doesn't apply to Gmail - start fresh.
      password: '',
    });
  } else {
    // OAuth is Outlook-only (Gmail goes through the app-password mode).
    set({
      ...identityReset,
      service: 'Outlook.com',
      auth_method: 'OAuth',
      ...OAUTH_SMTP,
    });
  }
};

  const applyConnectedApp = useCallback((appName: string) => {
    setF((prev) => ({
      ...prev,
      connected_app: appName,
      service: 'Outlook.com',
      ...OAUTH_SMTP,
    }));
  }, []);


  const noConnectedApp =
    isOAuth && !loading && !appsError && apps.length === 0 && !f.connected_app;

  useEffect(() => {
    if (loading || !isOAuth || f.connected_app || apps.length === 0) return;
    applyConnectedApp(apps[0].name);
  }, [loading, isOAuth, f.connected_app, apps, applyConnectedApp]);

  useEffect(() => {
    if (loading || !isOAuth) return;
    if (f.smtp_server?.trim() && f.smtp_port?.trim()) return;
    set(OAUTH_SMTP);
  }, [loading, isOAuth, f.smtp_server, f.smtp_port]); // eslint-disable-line react-hooks/exhaustive-deps

  // Same backfill for Gmail. The host/port inputs are no longer rendered, so a
  // saved account that arrives with them blank would otherwise leave Save and
  // Test permanently disabled with no field to fix them in.
  useEffect(() => {
    if (loading || isOAuth || !isGmail) return;
    if (f.smtp_server?.trim() && f.smtp_port?.trim()) return;
    set(GMAIL_SMTP);
  }, [loading, isOAuth, isGmail, f.smtp_server, f.smtp_port]); // eslint-disable-line react-hooks/exhaustive-deps

  const persist = async (): Promise<string | null> => {
    if (!f.email_id.trim()) {
      toast.warning('Email address is required.');
      return null;
    }
    if (isOAuth && !f.connected_app) {
      toast.warning(
        'No Connected App is configured. Please contact the administrator.',
      );
      return null;
    }
    const { ...payload } = f;
    const res = await brainConfigService.saveEmailAccount({
      ...payload,
      email_id: f.email_id.trim(),
      name: savedName || undefined,
      // Only one account is supported, so it's always the default sender.
      default_outgoing: true,
      // Always verify on save - Frappe runs the live SMTP/connection check.
      awaiting_password: false,
      password: f.password || undefined,
    });
    if (!res.ok || !res.name) {
      toast.error(res.error || 'Could not save the account');
      return null;
    }
    setSavedName(res.name);
    // Re-read what was actually stored so `original` is the new baseline -
    // otherwise the form stays permanently dirty and Save never settles into
    // its "nothing left to save" state.
    try {
      await reload(res.name);
    } catch (err) {
      logger.error('Could not re-read the saved account', err);
    }
    return res.name;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const name = await persist();
      if (name) {
        toast.success('Email account saved');
        onSaved(name);
      }
    } catch (err) {
      logger.error('Save email account failed', err);
      toast.error(getFriendlyErrorMessage(err, 'Could not save the account'));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      // Tests the draft as typed - nothing is written, so a failed test can't
      // leave broken settings saved. `name` only lets the backend reuse the
      // stored OAuth token / an unchanged password for an existing account.
      const res = await brainConfigService.testEmailDraft({
        ...f,
        email_id: f.email_id.trim(),
        name: savedName || undefined,
        password: f.password || undefined,
      });
      if (res.ok) {
        toast.success(
          res.recipient ? `Test email sent to ${res.recipient}` : 'Test email sent',
        );
      } else {
        toast.error(res.error || 'Connection failed');
      }
    } catch (err) {
      logger.error('Test email account failed', err);
      toast.error(getFriendlyErrorMessage(err, 'Could not test the connection'));
    } finally {
      setTesting(false);
    }
  };

  const handleAdoptIdentity = async () => {
    if (!mismatch || !savedName) return;
    setAdopting(true);
    try {
      const res = await brainConfigService.adoptEmailOAuthIdentity(
        savedName,
        mismatch.confirmToken,
      );
      setMismatch(null);
      toast.success(`Connected as ${res.identity}`);
      // reload(savedName), NOT init() - the account may have been created inside
      // this dialog, in which case the accountName prop is still null and init()
      // would reset the form to a blank "Add email account".
      await reload(savedName);
      onSaved(savedName);
    } catch (err) {
      logger.error('Adopt OAuth identity failed', err);
      toast.error(getFriendlyErrorMessage(err, 'Could not switch to that account'));
    } finally {
      setAdopting(false);
    }
  };

  const handleAuthorize = async () => {
    setAuthorizing(true);
    setAuthPhase('saving');
    setAuthNotice(null);
    setMismatch(null);
    const wasNew = !savedName;
    try {
      const name = await persist(); // ensure latest connected_app is stored
      if (!name) return;
      // The consent flow binds the token to a stored record, so authorizing
      // necessarily saves first. Announce it and refresh the parent list now -
      // otherwise abandoning consent leaves an account the operator never saw
      // created, and can't see until they reopen the page.
      toast.success(wasNew ? 'Email account saved' : 'Changes saved');
      onSaved(name);
      setAuthPhase('consent');
      const { authorize_url } = await brainConfigService.authorizeEmailAccount(name);
      const result = await runEmailOAuthPopup(authorize_url, name);
      if (result.ok) {
        toast.success('Authorized');
        const acc = await brainConfigService.getEmailAccount(name);
        set({ oauth_connected: acc.oauth_connected });
        onSaved(name);
        // A newly authorized sending account can complete workspace setup -
        // let the sidebar lift the setup lock without a reload.
        notifySetupStatusChanged();
      } else if (result.identityMismatch && result.confirmToken) {
        // Not an error - offer to adopt the account they actually signed in with.
        setMismatch({
          identity: result.identity || '',
          expected: result.expected || f.email_id.trim(),
          confirmToken: result.confirmToken,
        });
      } else if (result.adminConsent) {
        // Not a failure - the tenant admin must approve, then the user retries.
        setAuthNotice(
          result.message ||
            'Your organisation requires an administrator to approve Orgain ' +
              'before this mailbox can send. Once approved, click Authorize again.',
        );
      } else {
        toast.error('Authorization did not complete');
      }
    } catch (err) {
      logger.error('Authorize failed', err);
      toast.error(getFriendlyErrorMessage(err, 'Could not authorize'));
    } finally {
      setAuthorizing(false);
    }
  };

  // ---- Reusable field groups, composed per-service below ------------------

  const passwordField = (label: string, hint: string) => (
    <div className="space-y-1">
      <Label className="text-xs uppercase text-muted-foreground">{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          type={showPwd ? 'text' : 'password'}
          value={f.password}
          onChange={(e) => {
            const v = e.target.value;
            set({ password: v });
            if (!v) setShowPwd(false);
          }}
          placeholder={f.has_password ? '•••••• (unchanged)' : label}
        />
        {f.password ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setShowPwd((v) => !v)}
            aria-label={showPwd ? 'Hide password' : 'Show password'}
          >
            {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
        ) : null}
      </div>
      <p className="text-[11px] text-muted-foreground">{hint}</p>
    </div>
  );


  // Fields the operator can actually change. Server-owned flags
  // (oauth_connected, has_password, can_authorize, owner) are excluded - they
  // move on their own and would otherwise read as unsaved edits.
  const EDITABLE = [
    'email_id', 'email_account_name', 'service', 'auth_method', 'connected_app',
    'password', 'enable_outgoing', 'smtp_server', 'smtp_port', 'use_tls',
    'use_ssl_for_outgoing', 'always_bcc', 'send_unsubscribe_message',
    'track_email_status', 'no_smtp_authentication', 'enable_incoming', 'use_ssl',
    'use_starttls', 'email_server', 'incoming_port', 'email_sync_option',
    'initial_sync_count', 'attachment_limit', 'add_signature', 'signature',
    'enable_auto_reply', 'auto_reply_message', 'footer',
  ] as const;
  const snapshot = (x: Partial<Form>) => JSON.stringify(EDITABLE.map((k) => x[k] ?? null));
  // A never-saved account is always savable; a saved one only when something
  // actually changed - so Save stops offering a no-op re-post of identical data.
  const dirty = !original || snapshot(f) !== snapshot(original);

  const canAuthorize = !(f.can_authorize === false && !!savedName);

  // An OAuth mailbox can't send until consent is granted, so saving or testing
  // one would only surface a confusing SMTP failure. Authorize is the way out:
  // it persists the draft itself before opening the consent popup.
  const oauthUnauthorized = isOAuth && !f.oauth_connected;
  const oauthUnauthorizedHint = 'Authorize this mailbox first.';

  const providerLabel =
    apps.find((a) => a.name === f.connected_app)?.provider_name ||
    f.connected_app ||
    'OAuth provider';


  const oauthIdentity = (
    <div className="flex items-center gap-3 rounded-md border p-3">
      <div
        className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md border ${
          f.oauth_connected
            ? 'border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/40'
            : 'bg-muted'
        }`}
      >
        <ShieldCheck
          className={`h-4 w-4 ${f.oauth_connected ? 'text-green-600' : 'text-muted-foreground'}`}
        />
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium leading-none">{providerLabel}</p>
        <p className="mt-1.5 flex items-center gap-1.5 text-xs">
          {f.oauth_connected ? (
            <>
              <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 text-green-600" />
              <span className="text-green-700 dark:text-green-500">Connected</span>
            </>
          ) : (
            <>
              <XCircle className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
              <span className="text-muted-foreground">Not connected</span>
            </>
          )}
          {/* When connected, show the identity that ACTUALLY consented (read from
              the token) - not the typed address. Echoing email_id here is what
              let a sign-in as the wrong Microsoft account read as correct. */}
          {(f.oauth_connected ? f.oauth_identity || f.email_id : f.email_id).trim() && (
            <span className="truncate text-muted-foreground">
              · {(f.oauth_connected ? f.oauth_identity || f.email_id : f.email_id).trim()}
            </span>
          )}
        </p>
      </div>

      {canAuthorize && (
        <Button
          type="button"
          size="sm"
          variant={f.oauth_connected ? 'outline' : 'default'}
          className="flex-shrink-0"
          onClick={handleAuthorize}
          disabled={authorizing || !f.connected_app || !f.email_id.trim()}
        >
          {authorizing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {authPhase === 'saving' ? 'Saving…' : 'Authorizing…'}
            </>
          ) : f.oauth_connected ? (
            'Re-authorize'
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" /> Save &amp; authorize
            </>
          )}
        </Button>
      )}
    </div>
  );

  const oauthBlock = (
    <div className="space-y-3">
      {noConnectedApp ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>No Connected App configured</AlertTitle>
          <AlertDescription>
            OAuth cannot be set up until an administrator configures one. Please
            contact the administrator for more details.
          </AlertDescription>
        </Alert>
      ) : (
        <>
          {/* A failed lookup, not a missing app - offer a retry rather than
              sending the operator to an administrator over a transient error. */}
          {appsError && (
            <Alert className="border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200 [&>svg]:text-amber-600">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between gap-3">
                <span>Could not load the Connected App configuration.</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="flex-shrink-0"
                  onClick={() => void loadApps()}
                  disabled={appsLoading}
                >
                  {appsLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Retry'}
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* A saved account carries its own `connected_app`, so it can still
              (re)authorize even while the app list is unavailable. */}
          {(!appsError || f.connected_app) && oauthIdentity}

          {/* Save is disabled until consent is granted, so this button is the
              only way a new OAuth account gets written - say that up front
              rather than letting it save behind the operator's back. */}
          {!f.oauth_connected && (!appsError || f.connected_app) && (
            <p className="text-xs text-muted-foreground">
              This saves the account, then opens the provider's sign-in window to
              grant consent.
            </p>
          )}

          {!canAuthorize && (
            <p className="text-xs text-muted-foreground">
              Only {f.owner || 'the admin who created this account'} can re-authorize
              this mailbox.
            </p>
          )}

          {/* Signed in as someone else. Nothing has been applied - the token is
              parked server-side - so this is a choice, not an error. */}
          {mismatch && (
            <Alert className="border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200 [&>svg]:text-amber-600">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>You signed in as {mismatch.identity}</AlertTitle>
              <AlertDescription className="space-y-3">
                <p>
                  This mailbox is set to {mismatch.expected}. Use{' '}
                  {mismatch.identity} as this mailbox instead? It becomes the
                  sender address on every notification email.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleAdoptIdentity}
                    disabled={adopting}
                  >
                    {adopting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Switching…
                      </>
                    ) : (
                      `Use ${mismatch.identity}`
                    )}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setMismatch(null)}
                    disabled={adopting}
                  >
                    Cancel
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {authNotice && (
            <Alert className="border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200 [&>svg]:text-amber-600">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{authNotice}</AlertDescription>
            </Alert>
          )}
        </>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dark max-w-2xl max-h-[88vh] flex flex-col overflow-hidden bg-background text-foreground">
        <DialogHeader>
          {/* Keyed off savedName, not the accountName prop: authorizing creates
              the record mid-dialog, so "Add" would keep lying about a mailbox
              that already exists. */}
          <DialogTitle>{savedName ? 'Edit email account' : 'Add email account'}</DialogTitle>
          <DialogDescription>
            Configure the mailbox that sends notification emails.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <div className="space-y-5">
            {/* This mailbox exists purely to send notifications, so the
                "Enable sending" toggle gates the whole form - off means
                there's nothing left to configure. */}
            <div className="flex items-center justify-between rounded-md border p-4">
              <div>
                <Label className="text-base">Enable sending</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  This mailbox sends notification emails. Turn off to keep it
                  saved but inactive.
                </p>
              </div>
              <Switch
                checked={!!f.enable_outgoing}
                onCheckedChange={(c) => set({ enable_outgoing: c })}
              />
            </div>

            {f.enable_outgoing && (
              <>
            {/* Connection mode drives everything below it. */}
            <section className="space-y-3">
              <div className="space-y-1 md:max-w-xs">
                <Label className="text-xs uppercase text-muted-foreground">Service</Label>
                <Select value={mode} onValueChange={(v) => onModeChange(v as ConnMode)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="dark">
                    {MODES.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </section>

            <Separator />

            {/* Account identity - always needed regardless of service. */}
            <section className="space-y-3">
              <Label className="text-base">Account</Label>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1">
                  <Label className="text-xs uppercase text-muted-foreground">
                    Email address <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    type="email"
                    value={f.email_id}
                    onChange={(e) => set({ email_id: e.target.value })}
                    placeholder="alerts@yourcompany.com"
                    maxLength={254}
                    aria-invalid={!f.email_id.trim()}
                    className={
                      !f.email_id.trim()
                        ? 'border-destructive focus-visible:ring-destructive'
                        : ''
                    }
                  />
                  {!f.email_id.trim() && (
                    <p className="text-[11px] text-destructive">Email address is required.</p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase text-muted-foreground">Account name</Label>
                  <Input
                    value={f.email_account_name || ''}
                    onChange={(e) => set({ email_account_name: e.target.value })}
                    placeholder='e.g. "Support"'
                    disabled={!!savedName}
                    title={savedName ? "The account name can't be changed after the account is created." : undefined}
                  />
                </div>
              </div>
            </section>

            <Separator />

            {/* Connection - the shape depends on the selected mode. */}
            <section className="space-y-3">
              {/* Custom server: one simple group - SMTP host/port + password. */}
              {mode === 'custom' && (
                <>
                  <Label className="text-base">Server</Label>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="space-y-1">
                      <Label className="text-xs uppercase text-muted-foreground">
                        SMTP server <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        value={f.smtp_server || ''}
                        onChange={(e) => set({ smtp_server: e.target.value })}
                        placeholder="smtp.example.com"
                        aria-invalid={!f.smtp_server?.trim()}
                        className={!f.smtp_server?.trim() ? 'border-destructive focus-visible:ring-destructive' : ''}
                      />
                      {!f.smtp_server?.trim() && (
                        <p className="text-[11px] text-destructive">SMTP server is required.</p>
                      )}
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs uppercase text-muted-foreground">
                        Port <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        value={f.smtp_port || ''}
                        onChange={(e) => set({ smtp_port: e.target.value })}
                        className={`w-32 ${!f.smtp_port?.trim() ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                        aria-invalid={!f.smtp_port?.trim()}
                      />
                      {!f.smtp_port?.trim() && (
                        <p className="text-[11px] text-destructive">Port is required.</p>
                      )}
                    </div>
                    <div className="md:col-span-2 md:max-w-md">
                      {passwordField('Password', 'SMTP password. The connection is verified on save.')}
                    </div>
                    <div className="flex items-center justify-between rounded-md border p-2">
                      <Label className="text-xs">Use TLS</Label>
                      <Switch checked={!!f.use_tls} onCheckedChange={(c) => set({ use_tls: c })} />
                    </div>
                    <div className="flex items-center justify-between rounded-md border p-2">
                      <Label className="text-xs">Use SSL</Label>
                      <Switch
                        checked={!!f.use_ssl_for_outgoing}
                        onCheckedChange={(c) => set({ use_ssl_for_outgoing: c })}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Gmail (app password): the SMTP host/port are fixed values the
                  operator can neither choose nor usefully override, so - as with
                  OAuth/Outlook below - they aren't shown. They live in form state
                  (see GMAIL_SMTP) and are still sent on save. Only the Google App
                  Password is asked for. */}
              {mode === 'gmail_app' && (
                <>
                  <Label className="text-base">Authentication</Label>
                  <div className="md:max-w-md">
                    {passwordField(
                      'App password',
                      'Create a Google App Password and paste it here. The connection is verified on save.',
                    )}
                  </div>
                </>
              )}

              {/* OAuth: the Connected App defines the provider (Gmail /
                  Outlook), so there's no separate provider picker here. */}
              {mode === 'oauth' && (
                <>
                  <Label className="text-base">Authentication</Label>
                  {oauthBlock}
                </>
              )}
            </section>

            {/* OAuth has no Server section: the Connected App pins the provider
                to Outlook, so the SMTP host / port / TLS are fixed values the
                operator can neither choose nor usefully override. They stay in
                form state (see OAUTH_SMTP) and are still sent on save. */}

            {/* Incoming (POP/IMAP) intentionally hidden for now - outgoing
                notifications don't need a receiving mailbox. Form state +
                backend fields are kept so it can be restored later. */}

            {/* Signature / Auto Reply / Footer intentionally hidden for now.
                Form state + backend fields are kept so it can be restored. */}
              </>
            )}
          </div>
        )}

        </DialogBody>

        {/* gap-2 spaces the stacked buttons on mobile (flex-col); sm:gap-0
            keeps the desktop row spacing (space-x-2) unchanged. */}
        <DialogFooter className="gap-2 sm:gap-0">
          {/* "Done" once everything is stored - "Close" next to a dead Save
              button reads like walking away from unsaved work. */}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving || testing}>
            {savedName && !dirty ? 'Done' : 'Close'}
          </Button>
          {/* Test connection only makes sense for a sending mailbox -
              hide it entirely when "Enable sending" is off. */}
          {f.enable_outgoing && (
            <Button
              variant="outline"
              onClick={handleTest}
              title={oauthUnauthorized ? oauthUnauthorizedHint : undefined}
              disabled={
                saving ||
                testing ||
                loading ||
                !f.email_id.trim() ||
                (isOAuth && !f.connected_app) ||
                oauthUnauthorized ||
                (!f.smtp_server?.trim() || !f.smtp_port?.trim())
              }
            >
              {testing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Testing…
                </>
              ) : (
                <>
                  <Beaker className="mr-2 h-4 w-4" /> Test connection
                </>
              )}
            </Button>
          )}
          <Button
            onClick={handleSave}
            title={
              !!f.enable_outgoing && oauthUnauthorized
                ? oauthUnauthorizedHint
                : !dirty
                  ? 'No changes to save.'
                  : undefined
            }
            disabled={
              saving ||
              testing ||
              loading ||
              !dirty ||
              !f.email_id.trim() ||
              (isOAuth && !f.connected_app) ||
              (!!f.enable_outgoing &&
                (oauthUnauthorized || !f.smtp_server?.trim() || !f.smtp_port?.trim()))
            }
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" /> Save
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
